"use client";

import { getLangFromLocale } from "@/apiUtils/commonUtils";
import {
  PromotionTileBackgroundStyle,
  PromotionTilesType,
} from "@/common/lib/types";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { CLink } from "../common/customLink/CustomLink";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export interface CtaTileBlockProps {
  tileName?: string;
  ctaTiles: PromotionTilesType["ctaTiles"];
  backgroundStyle?: PromotionTileBackgroundStyle;
  locale: string;
  mobileHeight?: number;
  desktopHeight?: number;
}

export default function CtaTileBlock({
  tileName,
  ctaTiles,
  backgroundStyle,
  locale,
  mobileHeight,
  desktopHeight,
}: CtaTileBlockProps) {
  const lang = getLangFromLocale(locale) as "en" | "ar";
  const isSmallScreen = useMediaQuery("(max-width:768px)", {
    defaultValue: false,
    initializeWithValue: true,
  });

  const bgStyle: React.CSSProperties = {};
  if (backgroundStyle?.type === "color" && backgroundStyle?.color) {
    bgStyle.backgroundColor = backgroundStyle.color;
  }
  if (backgroundStyle?.type === "image") {
    const url =
      (isSmallScreen ? backgroundStyle.mobile : backgroundStyle.desktop)?.[
        lang as "en" | "ar"
      ] ||
      (isSmallScreen ? backgroundStyle.desktop : backgroundStyle.mobile)?.[
        lang as "en" | "ar"
      ];
    if (url) {
      bgStyle.backgroundImage = `url(${getCloudinaryURL(url)})`;
      bgStyle.backgroundSize = "cover";
      bgStyle.backgroundPosition = "center";
    }
  }
  if (mobileHeight != null && isSmallScreen) bgStyle.minHeight = `${mobileHeight}px`;
  if (desktopHeight != null && !isSmallScreen) bgStyle.minHeight = `${desktopHeight}px`;

  const items = Array.isArray(ctaTiles)
    ? ctaTiles.filter((item) => item.enable !== false)
    : [];

  const tileClassName =
    "inline-flex min-w-[88px] md:min-w-[100px] items-center justify-center rounded-sm border border-[#e5e5e5] bg-white px-4 py-3 text-center text-sm font-medium text-black transition hover:border-gray-400 whitespace-nowrap";

  const renderTiles = () =>
    items.map((item, idx) => {
      const href = item.link?.url ?? "#";
      const label =
        (item.title &&
          (item.title[lang] ?? item.title.en ?? item.title.ar)) ||
        "";
      if (!label) return null;
      return (
        <CLink key={item.id ?? idx} href={href} className={tileClassName}>
          {label}
        </CLink>
      );
    });

  return (
    <div
      className="flex w-full flex-col justify-center overflow-hidden px-4 py-6 md:px-6 md:py-8"
      style={Object.keys(bgStyle).length > 0 ? bgStyle : undefined}
    >
      {tileName && (
        <h2 className="mb-4 md:mb-8 text-center text-lg font-semibold md:text-[22px]">
          {tileName}
        </h2>
      )}
      <div
        className="md:flex md:justify-center overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div
          className="inline-grid w-max grid-rows-2 gap-2 md:gap-3"
          style={{ gridAutoFlow: "column", gridAutoColumns: "minmax(88px, auto)" }}
        >
          {renderTiles()}
        </div>
      </div>
    </div>
  );
}
